import React, { useRef } from 'react'
import styled, { css } from 'styled-components'
import CustomLink from '@Styles/CustomLink'
import { useGetAllInstructorsQuery } from '@Features/Instructor/InstructorApi/Instructor.api'
import { SkeletonLoadingAnimation } from '@Components/Utilities/Skeleton/Skeleton.styled'
import { useTranslation } from 'react-i18next'
import { Avatar as AbwAvatar, Icon as AbwIcon, TeacherTile } from '@Components/AbwaabPlus'
import { useStudentScreenSize } from '@eduact/student-theme'

/**
 * Uniform, on-brand instructor tile: shows the instructor photo when present,
 * otherwise initials on a tinted circle. Same link + data as before.
 */
const InstructorTile: React.FC<{ photo?: string | null; first?: string; last?: string; label?: string; size: number; tint?: string }> = ({
	photo,
	first,
	last,
	label,
	size,
	tint = 'var(--blue)',
}) => {
	const initials = `${first?.[0] ?? ''}${last?.[0] ?? ''}`.trim().toUpperCase()
	return (
		<Tile to={`/instructor/${label}`}>
			<AbwAvatar
				src={photo || undefined}
				emoji={!photo ? initials : undefined}
				color={tint}
				size={size}
				shape="circle"
				style={{
					border: '3px solid #fff',
					boxShadow: '0 0 0 1px var(--border), var(--sh-rest)',
					color: 'var(--blue)',
					fontWeight: 700,
					fontSize: Math.round(size * 0.34),
				}}
			/>
			<Name>
				{first} {last}
			</Name>
		</Tile>
	)
}

/** Admin ordering weight; anything without one sorts to the end, never the front. */
const weightOf = (row: BriefInstructor) => row.instructor?.weight ?? Number.MAX_SAFE_INTEGER

type CarouselProps = {
	/**
	 * 'rail' (default): the existing horizontal avatar rail — used by the
	 * logged-in landing, unchanged. 'grid': the guest mobile-first design —
	 * white teacher-tile cards, 3-up on phones / 6-up on desktop.
	 */
	variant?: 'rail' | 'grid'
	/** Overrides the grid variant's heading (e.g. the home's "المدرسون"). */
	heading?: React.ReactNode
}

const InstructorsCarousel: React.FC<CarouselProps> = ({ variant = 'rail', heading }) => {
	const { data, isLoading, isSuccess } = useGetAllInstructorsQuery()
	const [t, i18n] = useTranslation()
	/**
	 * Order by the admin's weight — 1 first, then 2, 3…
	 *
	 * The endpoint happens to return this order today, so the rail read
	 * correctly by accident: nothing here asked for it. Sorting explicitly means
	 * the first tiles are the intended ones no matter what the API returns, and
	 * a shared weight keeps the server's relative order (Array#sort is stable).
	 * The copy matters — RTK Query freezes its cached arrays, so sorting in
	 * place throws in development.
	 */
	const instructors = React.useMemo(() => {
		const rows = data?.data ?? []
		return [...rows].sort((a, b) => weightOf(a) - weightOf(b))
	}, [data?.data])
	const { isDesktop } = useStudentScreenSize()
	const railRef = useRef<HTMLDivElement>(null)
	const isRtl = i18n.dir() === 'rtl'

	// Page-scroll the rail; direction-aware so prev/next read correctly in both LTR and RTL.
	const scrollRail = (dir: 'prev' | 'next') => {
		const el = railRef.current
		if (!el) return
		const amount = el.clientWidth * 0.8
		const sign = dir === 'prev' ? (isRtl ? 1 : -1) : isRtl ? -1 : 1
		el.scrollBy({ left: sign * amount, behavior: 'smooth' })
	}

	// Centre the arrows on the avatar (which resizes with the breakpoint).
	const arrowTop = isDesktop ? 50 : 38
	const avatarSize = isDesktop ? 88 : 64

	if (variant === 'grid') {
		return (
			<CardsSection>
				<CardsHeadRow>
					<CardsHeading>
						{heading ?? (
							<>
								{t('landingPage:teachers_heading_pre', { defaultValue: 'نحن نتعاون مع' })}{' '}
								<span>{t('landingPage:teachers_heading_em', { defaultValue: 'أفضل المعلمين' })}</span>
							</>
						)}
					</CardsHeading>
					<HeadControls>
						<ArrowGroup>
							<RoundArrow type="button" aria-label="prev" onClick={() => scrollRail('prev')}>
								<span style={{ transform: isRtl ? 'scaleX(-1)' : 'none', display: 'flex' }}>
									<AbwIcon name="chevron-left" size={15} />
								</span>
							</RoundArrow>
							<RoundArrow type="button" aria-label="next" onClick={() => scrollRail('next')}>
								<span style={{ transform: isRtl ? 'none' : 'scaleX(-1)', display: 'flex' }}>
									<AbwIcon name="chevron-left" size={15} />
								</span>
							</RoundArrow>
						</ArrowGroup>
						<SeeAll to="/search?tab=instructors">
							{t('landingPage:courses.see_all', { defaultValue: 'عرض الكل' })}
							<SeeAllChevron>
								<AbwIcon name="chevron-left" size={14} />
							</SeeAllChevron>
						</SeeAll>
					</HeadControls>
				</CardsHeadRow>
				<CardsRail ref={railRef}>
					{isLoading
						? Array.from({ length: 6 }).map((_, i) => (
								<SkeletonTile key={`instructor-skeleton-${i}`}>
									<SkeletonDot style={{ width: 64, height: 64 }} />
									<SkeletonBar />
								</SkeletonTile>
						  ))
						: isSuccess &&
						  instructors.map((instructor, index) => (
								<TeacherTile
									key={`${instructor.lms_id}-${index}-instructor`}
									as={CustomLink}
									to={`/instructor/${instructor.instructor.label}`}
									name={`${instructor.first_name} ${instructor.last_name}`}
									subject={(instructor.instructor as any)?.category?.name}
									photo={instructor.profile_photo || undefined}
									initials={`${instructor.first_name?.[0] ?? ''}${instructor.last_name?.[0] ?? ''}`.trim().toUpperCase()}
									tint={index % 3 === 2 ? 'var(--yellow)' : 'var(--blue)'}
								/>
						  ))}
				</CardsRail>
			</CardsSection>
		)
	}

	return (
		<Section>
			<Heading>
				{t('landingPage:collaborate')} <span>{t('landingPage:best')}</span>
			</Heading>
			<RailWrap>
				<Rail ref={railRef}>
					{isLoading
						? Array.from({ length: 8 }).map((_, i) => (
								<SkeletonTile key={`instructor-skeleton-${i}`}>
									<SkeletonDot style={{ width: avatarSize, height: avatarSize }} />
									<SkeletonBar />
								</SkeletonTile>
						  ))
						: isSuccess &&
						  instructors.map((instructor, index) => (
								<InstructorTile
									key={`${instructor.lms_id}-${index}-instructor`}
									photo={instructor.profile_photo}
									first={instructor.first_name}
									last={instructor.last_name}
									label={instructor.instructor.label}
									size={avatarSize}
									tint={index % 3 === 2 ? 'var(--yellow)' : 'var(--blue)'}
								/>
						  ))}
				</Rail>
				<Arrow className="prev" type="button" aria-label="prev" style={{ top: arrowTop }} onClick={() => scrollRail('prev')}>
					<span style={{ transform: isRtl ? 'scaleX(-1)' : 'none', display: 'flex' }}>
						<AbwIcon name="chevron-left" size={20} />
					</span>
				</Arrow>
				<Arrow className="next" type="button" aria-label="next" style={{ top: arrowTop }} onClick={() => scrollRail('next')}>
					<span style={{ transform: isRtl ? 'none' : 'scaleX(-1)', display: 'flex' }}>
						<AbwIcon name="chevron-left" size={20} />
					</span>
				</Arrow>
			</RailWrap>
		</Section>
	)
}

export default InstructorsCarousel

const Section = styled.section`
	max-width: 1180px;
	margin: 40px auto 0;
	padding: 0 16px;
	@media (min-width: 768px) {
		margin-top: 48px;
		padding: 0 24px;
	}
`
const Heading = styled.h2`
	margin: 0 0 20px;
	text-align: start;
	font: var(--fw-regular) 18px var(--font-sans);
	color: var(--text-primary);
	span {
		font-weight: var(--fw-bold);
		color: var(--blue);
		text-decoration: underline;
		text-underline-offset: 4px;
	}
	@media (min-width: 768px) {
		font-size: 22px;
	}
`
/* Guest design variant: teacher-tile cards in a swipeable carousel —
   3 cards visible per mobile viewport (matching the design grid), 6 on desktop. */
const CardsSection = styled.section`
	padding: 20px 16px 4px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`
const CardsHeadRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`
const HeadControls = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: none;
`
/* Design's desktop rail controls: 28px round bordered prev/next buttons. */
const ArrowGroup = styled.div`
	display: none;
	@media (min-width: 1024px) {
		display: flex;
		gap: 4px;
	}
`
const RoundArrow = styled.button`
	width: 28px;
	height: 28px;
	border: 1px solid var(--border);
	border-radius: var(--r-full);
	background: var(--surface-card);
	color: var(--text-secondary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: box-shadow var(--dur-fast) var(--ease);
	&:hover {
		box-shadow: var(--sh-hover);
	}
`
const SeeAll = styled(CustomLink)`
	display: inline-flex;
	align-items: center;
	gap: 2px;
	font: var(--fw-bold) 13px var(--font-sans);
	color: var(--blue);
	white-space: nowrap;
`
const SeeAllChevron = styled.span`
	display: flex;
	html:not([dir='rtl']) & {
		transform: scaleX(-1);
	}
`
const CardsHeading = styled.h2`
	margin: 0;
	text-align: start;
	font: var(--fw-bold) var(--fs-body-lg) var(--font-sans);
	color: var(--text-primary);
	span {
		color: var(--blue);
	}
	@media (min-width: 1024px) {
		font-size: 22px;
	}
`
const CardsRail = styled.div`
	/* Mobile/tablet: a 3-up grid that fills ROW by row, so the tiles read in
	   weight order across the row — the direction the search results use.
	   grid-auto-flow: column filled top-to-bottom instead, which laid weights
	   1..6 out as columns (1,3,5 across the first row, 2,4,6 beneath) and read
	   as though the list were unsorted.

	   Two rows are shown and the rest is behind عرض الكل; capping in CSS rather
	   than slicing in JS keeps the cut tied to this same breakpoint. */
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 8px;
	padding-bottom: 6px;
	@media (max-width: 1023px) {
		> *:nth-child(n + 7) {
			display: none;
		}
	}
	@media (min-width: 1024px) {
		/* Desktop keeps the original single scrollable row of 168px cards. */
		display: flex;
		overflow-x: auto;
		scroll-snap-type: x proximity;
		scrollbar-width: none;
		-ms-overflow-style: none;
		&::-webkit-scrollbar {
			display: none;
		}
		> * {
			flex: 0 0 168px;
			scroll-snap-align: start;
		}
	}
`

/* Side padding seats the nav arrows in the gutters, clear of the avatars —
   same pattern as ClassroomRail (arrows visible at every breakpoint). */
const RailWrap = styled.div`
	position: relative;
	padding-inline: 40px;
	@media (min-width: 768px) {
		padding-inline: 52px;
	}
`
const Rail = styled.div`
	display: flex;
	gap: 8px;
	overflow-x: auto;
	padding-bottom: 6px;
	scroll-snap-type: x proximity;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
	@media (min-width: 768px) {
		gap: 20px;
	}
`
const Tile = styled(CustomLink)`
	flex: 0 0 auto;
	scroll-snap-align: start;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	width: 96px;
	padding: 6px 0;
	text-align: center;
	@media (min-width: 768px) {
		width: 120px;
	}
`
const Name = styled.span`
	font: var(--fw-bold) 13px var(--font-sans);
	color: var(--text-primary);
`

/* Loading skeleton — same Tile geometry as a real instructor so it lines up
   with the nav arrows (avatar centre) exactly as the loaded rail does. */
const shimmer = css`
	position: relative;
	overflow: hidden;
	background: var(--surface-sunken-strong);
	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
		animation: ${SkeletonLoadingAnimation} 1.6s infinite;
	}
`
const SkeletonTile = styled.div`
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	width: 96px;
	padding: 6px 0;
	@media (min-width: 768px) {
		width: 120px;
	}
`
const SkeletonDot = styled.span`
	flex: none;
	border-radius: 50%;
	${shimmer}
`
const SkeletonBar = styled.span`
	width: 70px;
	height: 12px;
	border-radius: 6px;
	${shimmer}
`
const Arrow = styled.button`
	display: flex;
	position: absolute;
	/* top is set inline to the avatar centre (varies with avatar size) */
	transform: translateY(-50%);
	z-index: 2;
	width: 36px;
	height: 36px;
	border-radius: 50%;
	border: 1px solid var(--border);
	background: var(--surface-card);
	color: var(--blue);
	cursor: pointer;
	align-items: center;
	justify-content: center;
	box-shadow: var(--sh-hover);
	&.prev {
		inset-inline-start: 0;
	}
	&.next {
		inset-inline-end: 0;
	}
	@media (min-width: 768px) {
		width: 40px;
		height: 40px;
	}
`
